import { useEffect } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { pinService, type PinStatus } from "@/services/pinService";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface PinVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
}

export function PinVerificationDialog({
  open,
  onOpenChange,
  onSuccess,
  title = "Enter PIN",
  description = "Enter your security PIN to continue",
}: PinVerificationDialogProps) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  // Reset state whenever the dialog opens
  useEffect(() => {
    if (open) {
      setPin("");
      setError("");
      setIsVerifying(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = pin.trim();
    if (!value) {
      setError("Please enter your PIN");
      return;
    }
    setError("");
    setIsVerifying(true);
    try {
      const VERIFY_TIMEOUT_MS = 15000;
      const result = await Promise.race([
        pinService.verifyPin(value),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Verification timed out. Please try again.")), VERIFY_TIMEOUT_MS)
        ),
      ]);
      if (result?.success) {
        queryClient.invalidateQueries({ queryKey: ["pin-status"] });
        setPin("");
        setError("");
        setIsVerifying(false);
        onSuccess?.();
        onOpenChange(false);
      } else {
        setError("Verification failed. Please try again.");
        setIsVerifying(false);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Verification failed. Please try again.";
      setError(message);
      setIsVerifying(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setPin("");
      setError("");
      setIsVerifying(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pin-verify">PIN</Label>
            <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
              <Input
                id="pin-verify"
                type={showPin ? "text" : "password"}
                placeholder="Enter your PIN"
                value={pin}
                onChange={(e) =>
                  setPin(e.target.value.replace(/\D/g, "").slice(0, 8))
                }
                maxLength={8}
                autoFocus
                className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="input-pin-verify-dialog"
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                data-testid="button-toggle-pin-dialog"
              >
                {showPin ? (
                  <EyeOff className="h-4 w-4 shrink-0" />
                ) : (
                  <Eye className="h-4 w-4 shrink-0" />
                )}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              data-testid="button-cancel-pin-dialog"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isVerifying || !pin.trim()}
              data-testid="button-submit-pin-dialog"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Unlock"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ThreadLockButtonProps {
  threadId: string;
  isLocked: boolean;
  onLockChange?: (locked: boolean) => void;
}

export function ThreadLockButton({
  threadId,
  isLocked,
  onLockChange,
}: ThreadLockButtonProps) {
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<"lock" | "unlock" | null>(
    null
  );
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();

  const {
    data: pinStatus,
    isPending,
    isError,
    error: pinStatusError,
  } = useQuery<PinStatus>({
    queryKey: ["pin-status"],
    queryFn: () => pinService.getStatus(),
  });

  const toggleLockMutation = useMutation({
    mutationFn: async ({
      threadId,
      lock,
    }: {
      threadId: string;
      lock: boolean;
    }) => {
      const { chatThreadsService } = await import("@/services/chatThreadsService");
      return await chatThreadsService.lockChatThread(threadId, lock, currentWorkspaceId ?? null);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["chat-threads"] });
      onLockChange?.(variables.lock);
    },
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isPending) {
      return;
    }

    if (isError) {
      toast({
        title: "Could not verify PIN status",
        description:
          pinStatusError instanceof Error
            ? pinStatusError.message
            : "Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (pinStatus == null) {
      return;
    }

    if (!pinStatus.hasPinSet) {
      window.location.href = "/settings?tab=security";
      return;
    }

    const action = isLocked ? "unlock" : "lock";
    setPendingAction(action);

    if (!pinStatus.isSessionValid) {
      setShowPinDialog(true);
      return;
    }

    toggleLockMutation.mutate({ threadId, lock: !isLocked });
  };

  const handlePinSuccess = () => {
    if (pendingAction) {
      toggleLockMutation.mutate({ threadId, lock: pendingAction === "lock" });
    }
    setPendingAction(null);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={handleClick}
        disabled={toggleLockMutation.isPending}
        title={isLocked ? "Unlock thread" : "Lock thread"}
        data-testid={`button-lock-thread-${threadId}`}
      >
        <Lock
          className={`h-4 w-4 ${isLocked ? "text-amber-500" : "text-muted-foreground"}`}
        />
      </Button>

      <PinVerificationDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onSuccess={handlePinSuccess}
        title={pendingAction === "lock" ? "Lock Thread" : "Unlock Thread"}
        description={
          pendingAction === "lock"
            ? "Enter your PIN to lock this thread"
            : "Enter your PIN to unlock this thread"
        }
      />
    </>
  );
}

export function LockedThreadOverlay({
  onUnlock,
}: {
  onUnlock: () => void;
}) {
  const [showPinDialog, setShowPinDialog] = useState(false);

  return (
    <>
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="bg-amber-500/10 p-4 rounded-full mb-4">
          <Lock className="h-12 w-12 text-amber-500" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Thread is Locked</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-md">
          This thread is protected with a PIN. Enter your PIN to view its
          contents.
        </p>
        <Button onClick={() => setShowPinDialog(true)} data-testid="button-unlock-thread">
          <Lock className="h-4 w-4 mr-2" />
          Unlock Thread
        </Button>
      </div>

      <PinVerificationDialog
        open={showPinDialog}
        onOpenChange={setShowPinDialog}
        onSuccess={onUnlock}
        title="Unlock Thread"
        description="Enter your PIN to view this locked thread"
      />
    </>
  );
}
